<template>
  <modal :show.sync="showModal" header-classes="justify-content-center" @close="handleClose">
    <h4 slot="header" class="title title-up">
      {{ editingData ? 'Edit Gospel Presentation' : 'Log a Gospel Presentation' }}
    </h4>
    <!-- Presenter dropdown -->
    <el-row :gutter="20">
      <el-col :span="8">
        <label class="col-form-label top-margin">Select Presenter:</label>
      </el-col>
      <el-col :span="16">
        <el-select
          class="col select-default"
          filterable
          clearable
          v-model="newDisciplerPersonId"
          placeholder="Select Presenter..."
        >
          <el-option
            v-for="item in addableDisciplers"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-col>
      <span v-if="validationError.discipler" class="text-danger text-sm">
        Please select a presenter.
      </span>
    </el-row>

    <!-- Receiver dropdown -->
    <el-row :gutter="20" v-if="!editingData">
      <el-col :span="8">
        <label class="col-form-label top-margin">Select Receiver:</label>
      </el-col>
      <el-col :span="16">
        <el-select
          class="col select-default"
          filterable
          clearable
          v-model="newDisciplePersonId"
          placeholder="Select Receiver..."
        >
          <el-option
            v-for="item in possibleRecipients"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-col>
      <span v-if="validationError.disciple" class="text-danger text-sm">
        Please select a receiver.
      </span>
    </el-row>

    <hr class="divider" />

    <!-- Gospel Presentation -->
    <div class="section">
      <el-row :gutter="20" class="mb-2">
        <el-col :span="12">
          <label class="form-label">Date of Presentation:</label>
          <el-date-picker
            v-model="gospelPresentationDate"
            type="date"
            placeholder="Pick a date"
            style="width:100%"
          />
        </el-col>
        <el-col :span="12">
          <label class="form-label">Their Response:</label>
          <el-select
            v-model="gospelPresentationResponse"
            placeholder="Select Response"
            style="width: 100%"
          >
            <el-option label="Yes" value="Yes" />
            <el-option label="No" value="No" />
          </el-select>
        </el-col>
        <br><br>
        <el-col :span="24">
          <label class="form-label">Notes:</label>
          <el-input
            type="textarea"
            v-model="gospelPresentationNotes"
            placeholder="Enter any notes here..."
            rows="4"
            class="notes-textarea"
          />
          <p class="col-md-12 note">
            *Notes are public. Please do not share private/confidential information.
          </p>
        </el-col>
      </el-row>
    </div>

    <template slot="footer">
      <p-button @click.native="handleClose">
        Cancel
      </p-button>
      <p-button type="primary" v-if="!saving" @click.native="handleSave">
        Save
      </p-button>
      <p-button type="primary" v-if="saving">
        Saving...
      </p-button>
        &nbsp;&nbsp;
    </template>
  </modal>
</template>

<style scoped>
.top-margin {
  margin-top: 8px;
}
.align-center {
  align-items: center;
}
.section {
  margin-top: 0 !important;
  margin-bottom: 8px;
  padding-top: 0;
}
.divider {
  border: none;
  border-top: 1px solid #eee;
  margin: 24px 0;
}
.note {
  font-size: 1.1em;
  color: #FF2800;
  margin-bottom: 16px;
}
.form-label {
  font-weight: 500;
  margin-bottom: 4px;
  display: block;
}
.mb-2 {
  margin-bottom: 12px;
}
.notes-textarea {
  width: 100%;
}
</style>

.no-margin-row {
  margin-bottom: 0 !important;
  margin-top: 0 !important;
}

<script>
import Modal from '../UIComponents/Modal';
import PeopleRepository from '../../repositories/peopleRepository';
import { RepositoryFactory } from '../../repositories/repositoryFactory';

const DisciplesRepository = RepositoryFactory.get('disciples');

function validateDisciple(newDisciplePersonId) {
    return !!newDisciplePersonId;
}

export default {
    name: 'GospelPresentationModal',
    components: {
        Modal
    },
    props: {
        show: Boolean,
        editingData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            showModal: false,
            error: false,
            saving: false,

            newDisciplePersonId: null,
            possibleRecipients: [],
            newDisciplerPersonId: null,
            addableDisciplers: [],

            // Gospel Presentation fields
            gospelPresentationDate: new Date(),
            gospelPresentationResponse: null,
            gospelPresentationNotes: '',

            validationError: {
                disciple: false,
                discipler: false,
            }
        };
    },
    watch: {
        show(val) {
            this.showModal = val;
            if (val) {
                this.getAddableDisciplersList();
                // Only load receivers in create mode
                if (!this.editingData && this.newDisciplerPersonId) {
                    this.getPossibleRecipients(this.newDisciplerPersonId);
                }
            }
        },
        editingData: {
            handler(newData) {
                if (newData) {
                    // Populate form with editing data
                    this.gospelPresentationDate = newData.gospelPresentationDate || new Date();
                    this.gospelPresentationResponse = newData.gospelPresentationResponse;
                    this.gospelPresentationNotes = newData.gospelPresentationNotes;

                    // Set presenter but don't allow receiver editing in edit mode
                    this.newDisciplerPersonId = newData.presenterPersonId || null;
                    this.newDisciplePersonId = newData.receiverPersonId || newData.id || null;
                }
            },
            immediate: true
        },
        newDisciplerPersonId(val) {
            // Only load receivers list in create mode
            if (!this.editingData) {
                this.getPossibleRecipients(val);
                this.newDisciplePersonId = null;
            }
        },
        newDisciplePersonId(val) {
            if (!val && !this.editingData) {
                // Only reset fields in create mode when no receiver selected
                this.gospelPresentationDate = new Date();
                this.gospelPresentationResponse = null;
                this.gospelPresentationNotes = '';
            }
        }
    },
    methods: {
        getPresenterName(presenterId) {
            const selectedPresenter = this.addableDisciplers.find((d) => d.id === presenterId);
            return selectedPresenter ? selectedPresenter.name : '';
        },
        handleSave() {
            this.saving = true;

            // Validate presenter/receiver - only validate receiver in create mode
            this.validationError.disciple = !this.editingData && !validateDisciple(this.newDisciplePersonId);
            this.validationError.discipler = !validateDisciple(this.newDisciplerPersonId);

            if (this.validationError.disciple || this.validationError.discipler) {
                this.saving = false;
                return;
            }

            if (this.editingData) {
                // Edit mode - update existing record
                const payload = {
                    presenter: this.getPresenterName(this.newDisciplerPersonId),
                    gospel_presenter_person_id: this.newDisciplerPersonId,
                    gospel_presentation_answer: this.gospelPresentationResponse,
                    gospel_presentation_date: this.gospelPresentationDate,
                    gospel_presentation_notes: this.gospelPresentationNotes
                };

                this.$http.patch(`people/${this.editingData.id}/gospel-highcall`, payload)
                    .then((response) => {
                        this.saving = false;
                        this.$emit('discipleChange');
                        this.$message(response.data.message);
                        this.handleClose();
                    })
                    .catch((error) => {
                        this.saving = false;
                        this.$message.error(error.response.data.message);
                    });
            } else {
                // Create mode - get receiver info from possibleRecipients
                const selectedReceiver = this.possibleRecipients.find((r) => r.id === this.newDisciplePersonId);
                const receiverName = selectedReceiver ? selectedReceiver.name : '';
                const [firstName = '', lastName = ''] = receiverName.split(' ');

                // Find the presenter name from addableDisciplers
                const payload = {
                    first_name: firstName,
                    last_name: lastName,
                    presenter: this.getPresenterName(this.newDisciplerPersonId),
                    gospel_presenter_person_id: this.newDisciplerPersonId,
                    received_gospel_presentation: true,
                };

                if (this.gospelPresentationResponse === 'Yes' || this.gospelPresentationResponse === 'No') {
                    payload.gospel_presentation_answer = this.gospelPresentationResponse;
                }
                if (this.gospelPresentationDate) {
                    payload.gospel_presentation_date = this.gospelPresentationDate;
                }
                if (this.gospelPresentationNotes) {
                    payload.gospel_presentation_notes = this.gospelPresentationNotes;
                }

                this.$http.patch(`people/${this.newDisciplePersonId}/gospel-highcall`, payload)
                    .then((response) => {
                        this.saving = false;
                        this.$emit('discipleChange');
                        this.$message(response.data.message);
                        this.handleClose();
                    })
                    .catch((error) => {
                        this.saving = false;
                        this.$message.error(error.response.data.message);
                    });
            }
        },

        handleClose() {
            this.newDisciplePersonId = null;
            this.newDisciplerPersonId = null;

            this.gospelPresentationDate = new Date();
            this.gospelPresentationResponse = null;
            this.gospelPresentationNotes = '';

            this.validationError.disciple = false;
            this.validationError.discipler = false;

            this.$emit('close');
        },
        getAddableDisciplersList() {
            DisciplesRepository.getAddableDisciplersList()
                .then((response) => {
                    this.addableDisciplers = response.data.data;
                })
                .catch((error) => {
                    this.error = error;
                });
        },
        getPossibleRecipients(presenterId) {
            if (!presenterId || !/^\d+$/.test(String(presenterId))) {
                this.possibleRecipients = [];
                return;
            }
            PeopleRepository.getPossibleInvitationRecipientsByPresenter(presenterId, 'gospel')
                .then((response) => {
                    this.possibleRecipients = response.data;
                })
                .catch(() => {
                    this.possibleRecipients = [];
                });
        }
    }
};
</script>
